.root {
  position: fixed;
  inset: 0;
  z-index: 9000;
  pointer-events: none;
}

.root.open {
  pointer-events: auto;
}

.overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  opacity: 0;
  transition: opacity 0.28s ease;
}

.root.open .overlay {
  opacity: 1;
}

.drawer {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: min(420px, 92vw);
  display: flex;
  flex-direction: column;
  overflow: hidden; /* header + footer pinned; only .lines scrolls */
  background: var(--color-bg-primary);
  border-left: 1px solid var(--color-border-default);
  box-shadow: -24px 0 60px -30px rgba(0, 0, 0, 0.8);
  transform: translateX(100%);
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.root.open .drawer {
  transform: translateX(0);
}

/* ──────────── HEADER ──────────── */
.header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px var(--page-padding);
  border-bottom: 1px solid var(--color-border-default);
}

.title {
  font-family: var(--font-syne);
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text-primary);
}

.close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: transparent;
  border: 1px solid var(--color-border-input);
  border-radius: var(--radius-sm);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;
}

.close:hover {
  color: var(--brand-ember);
  border-color: var(--brand-ember);
}

/* ──────────── EMPTY ──────────── */
.empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  padding: 24px;
}

.emptyText {
  font-family: var(--font-dm-sans);
  font-size: 1rem;
  color: var(--color-text-secondary);
}

.keepShopping {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  background: transparent;
  color: var(--brand-paper);
  border: 1px solid var(--brand-steel);
  border-radius: var(--radius-pill);
  font-family: var(--font-dm-sans);
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.25s ease, color 0.25s ease;
}

.keepShopping:hover {
  background: var(--brand-steel);
  color: var(--brand-slate);
}

/* ──────────── LINES ──────────── */
.lines {
  flex: 1;
  min-height: 0; /* REQUIRED: lets the flex child shrink so it scrolls internally
                    instead of overflowing and pushing the footer off-screen */
  overflow-y: auto;
  list-style: none;
  margin: 0;
  padding: 16px var(--page-padding);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.line {
  display: flex;
  gap: 14px;
  padding: 12px;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-lg);
}

.thumb {
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  object-fit: cover;
  background: var(--brand-paper);
  border-radius: var(--radius-sm);
}

.meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.name {
  font-family: var(--font-dm-sans);
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.variant {
  font-family: var(--font-dm-sans);
  font-size: 0.75rem;
  color: var(--color-text-secondary);
}

.controls {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 2px;
}

.qty {
  width: 52px;
  padding: 6px;
  text-align: center;
  background: transparent;
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-input);
  border-radius: var(--radius-sm);
  font-family: var(--font-dm-sans);
  font-size: 0.875rem;
}

.qty:focus {
  outline: none;
  border-color: var(--brand-ember);
}

.lineTotal {
  flex: 1;
  text-align: right;
  font-family: var(--font-dm-sans);
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--brand-ember);
}

.remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  color: var(--color-text-tertiary);
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;
}

.remove:hover {
  color: var(--brand-ember);
  border-color: var(--color-border-input);
}

/* ──────────── FOOTER ──────────── */
.footer {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px var(--page-padding);
  border-top: 1px solid var(--color-border-default);
}

.subtotal {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-family: var(--font-dm-sans);
  font-size: 1.1rem;
  color: var(--color-text-primary);
}

.subtotal strong {
  color: var(--brand-ember);
  font-variant-numeric: tabular-nums;
}

.note {
  font-family: var(--font-dm-sans);
  font-size: 0.75rem;
  color: var(--color-text-tertiary);
  margin: 0 0 4px;
}

.checkout {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px 24px;
  background: var(--brand-ember);
  color: var(--brand-paper);
  border: 1px solid var(--brand-ember);
  border-radius: var(--radius-pill);
  font-family: var(--font-dm-sans);
  font-size: 0.9375rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.checkout:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 40px -18px var(--brand-ember-soft);
}
